import { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { GpsSender } from './GpsSender';
import { usePermissions } from '../hooks/usePermissions';
import { subscribeToPush } from '../hooks/usePushNotifications';
import { api } from '../lib/api';
import { APP_VERSION } from '../lib/version';

const QRScanner = lazy(() => import('../components/qr/QRScanner').then(m => ({ default: m.QRScanner })));
const BusQRDisplay = lazy(() => import('../components/qr/BusQRDisplay').then(m => ({ default: m.BusQRDisplay })));
const CheckinHistory = lazy(() => import('../components/qr/CheckinHistory').then(m => ({ default: m.CheckinHistory })));

interface DriverPortalProps {
  onLogout: () => void;
  userName: string;
}

type Tab = 'track' | 'tripsheet' | 'hazard' | 'sos' | 'scan';

const HAZARD_TYPES = [
  { id: 'road_block', icon: '🚧', label: 'Road Block' },
  { id: 'accident', icon: '💥', label: 'Accident' },
  { id: 'flooding', icon: '🌊', label: 'Flooding' },
  { id: 'construction', icon: '🏗️', label: 'Construction' },
  { id: 'breakdown', icon: '🔧', label: 'Breakdown' },
  { id: 'other', icon: '⚠️', label: 'Other' },
];

export function DriverPortal({ onLogout, userName }: DriverPortalProps) {
  const { t } = useTranslation('driver');
  const [tab, setTab] = useState<Tab>('track');
  const [sheet, setSheet] = useState<any>(null);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [sosLoading, setSosLoading] = useState(false);
  const [sosSent, setSosSent] = useState(false);

  // Hazard state
  const [selectedHazard, setSelectedHazard] = useState<string | null>(null);
  const [hazardNote, setHazardNote] = useState('');
  const [hazardLoading, setHazardLoading] = useState(false);
  const [hazardSent, setHazardSent] = useState(false);
  const [busId, setBusId] = useState<string | null>(null);
  const [showBusQR, setShowBusQR] = useState(false);

  // Load bus ID for hazard reporting
  useEffect(() => {
    api.getBuses().then(({ buses }) => {
      if (buses.length > 0) setBusId(buses[0].id);
    }).catch(() => {});
  }, []);

  // Auto-subscribe driver to push notifications (for SOS alerts, trip reminders)
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'granted') {
      subscribeToPush();
    }
  }, []);

  const loadSheet = async () => {
    setTab('tripsheet');
    setSheetError(null);
    try {
      const r = await api.getDriverSheet();
      setSheet(r);
      if (r?.bus?.id) setBusId(r.bus.id);
    } catch (err: any) {
      setSheetError(err?.message || 'Failed to load trip sheet');
    }
  };

  const sendSOS = async () => {
    if (sosLoading) return;
    setSosLoading(true);
    try {
      const id = busId || sheet?.bus?.id;
      if (id) {
        await api.sendSOS({ bus_id: id, message: `Emergency SOS from driver ${userName}` });
      }
      setSosSent(true);
    } catch {} finally { setSosLoading(false); }
  };

  const reportHazard = async () => {
    if (!selectedHazard || !busId || hazardLoading) return;
    setHazardLoading(true);
    try {
      // Get current location
      let lat: number | undefined;
      let lng: number | undefined;
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
        });
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch { /* location not available */ }

      await api.reportHazard({
        bus_id: busId,
        hazard_type: selectedHazard,
        message: hazardNote || undefined,
        latitude: lat,
        longitude: lng,
      });
      setHazardSent(true);
    } catch {} finally { setHazardLoading(false); }
  };

  const resetHazard = () => {
    setSelectedHazard(null);
    setHazardNote('');
    setHazardSent(false);
  };

  if (tab === 'track') {
    return (
      <div className="driver-portal">
        <div className="driver-nav">
          <button className="tab-btn active" onClick={() => setTab('track')}>{t('tab_track')}</button>
          <button className="tab-btn" onClick={() => setTab('scan')}>{t('tab_scan')}</button>
          <button className="tab-btn" onClick={loadSheet}>{t('tab_tripsheet')}</button>
          <button className="tab-btn" onClick={() => { setTab('hazard'); resetHazard(); }}>{t('tab_hazard')}</button>
          <button className="tab-btn" onClick={() => setTab('sos')}>{t('tab_sos')}</button>
        </div>
        <GpsSender onLogout={onLogout} userName={userName} />
      </div>
    );
  }

  return (
    <div className="driver-portal">
      <div className="driver-nav">
        <button className="tab-btn" onClick={() => setTab('track')}>{t('tab_track')}</button>
        <button className={`tab-btn ${tab === 'scan' ? 'active' : ''}`} onClick={() => setTab('scan')}>{t('tab_scan')}</button>
        <button className={`tab-btn ${tab === 'tripsheet' ? 'active' : ''}`} onClick={loadSheet}>{t('tab_tripsheet')}</button>
        <button className={`tab-btn ${tab === 'hazard' ? 'active' : ''}`} onClick={() => { setTab('hazard'); resetHazard(); }}>{t('tab_hazard')}</button>
        <button className={`tab-btn ${tab === 'sos' ? 'active' : ''}`} onClick={() => setTab('sos')}>{t('tab_sos')}</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="version-tag">{APP_VERSION}</span>
          <button className="btn-logout" onClick={onLogout}>Logout</button>
        </div>
      </div>

      {tab === 'scan' && (
        <div style={{ padding: 16 }}>
          <Suspense fallback={<div className="admin-loading">Loading scanner...</div>}>
            <div className="qr-mode-toggle" style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <button className={`btn-${!showBusQR ? 'primary' : 'outline'}`} onClick={() => setShowBusQR(false)}>
                Scan Student QR
              </button>
              <button className={`btn-${showBusQR ? 'primary' : 'outline'}`} onClick={() => setShowBusQR(true)}>
                Show Bus QR
              </button>
            </div>
            {showBusQR ? (
              <BusQRDisplay />
            ) : busId ? (
              <>
                <QRScanner busId={busId} />
                <CheckinHistory busId={busId} />
              </>
            ) : (
              <div className="form-error">No bus assigned</div>
            )}
          </Suspense>
        </div>
      )}

      {tab === 'tripsheet' && (
        <div style={{ padding: 16 }}>
          {sheet ? (
            <div>
              <h2>Trip Sheet - {sheet.bus?.bus_number}</h2>
              {sheet.route && <p>Route: {sheet.route.name}</p>}
              <h3 style={{ marginTop: 16 }}>Students ({sheet.students?.length || 0})</h3>
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Grade</th><th>Stop</th><th>Parent</th><th>Phone</th></tr></thead>
                <tbody>
                  {(sheet.students || []).map((s: any) => (
                    <tr key={s.id} style={s.ride_cancelled ? { opacity: 0.4, textDecoration: 'line-through' } : {}}>
                      <td>{s.name}{s.ride_cancelled ? <span style={{ color: 'var(--danger)', fontSize: 11, marginLeft: 6, textDecoration: 'none', display: 'inline-block' }}>CANCELLED</span> : ''}</td>
                      <td>{s.grade || '-'}</td>
                      <td>{s.stop_name || '-'}</td><td>{s.parent_name || '-'}</td>
                      <td>{s.parent_phone || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sheet.stops?.length > 0 && (
                <>
                  <h3 style={{ marginTop: 16 }}>Stops</h3>
                  <div className="stops-list">
                    {sheet.stops.map((s: any) => (
                      <div key={s.id} className="stop-item">
                        <span className="stop-number">{s.stop_order}</span>
                        <span className="stop-name">{s.name}</span>
                        {s.estimated_time && <span className="stop-time">{s.estimated_time}</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : sheetError ? (
            <div className="form-error">{sheetError}</div>
          ) : (
            <div className="admin-loading">Loading trip sheet...</div>
          )}
        </div>
      )}

      {tab === 'hazard' && (
        <div className="hazard-page">
          <h2 className="hazard-title">Report Hazard</h2>

          {hazardSent ? (
            <div className="hazard-success">
              <div className="hazard-success-icon">✓</div>
              <h3>Hazard Reported!</h3>
              <p>All school admins have been notified.</p>
              <button className="btn-primary" onClick={resetHazard} style={{ marginTop: 20 }}>
                Report Another
              </button>
            </div>
          ) : (
            <>
              <p className="hazard-subtitle">Select the type of hazard</p>

              <div className="hazard-grid">
                {HAZARD_TYPES.map((h) => (
                  <button
                    key={h.id}
                    className={`hazard-type-btn ${selectedHazard === h.id ? 'hazard-selected' : ''}`}
                    onClick={() => setSelectedHazard(h.id)}
                  >
                    <span className="hazard-type-icon">{h.icon}</span>
                    <span className="hazard-type-label">{h.label}</span>
                  </button>
                ))}
              </div>

              <div className="hazard-note-group">
                <label>Additional notes (optional)</label>
                <textarea
                  className="hazard-note-input"
                  value={hazardNote}
                  onChange={(e) => setHazardNote(e.target.value)}
                  placeholder="Describe the hazard..."
                  rows={3}
                />
              </div>

              <button
                className="btn-primary hazard-submit-btn"
                onClick={reportHazard}
                disabled={!selectedHazard || hazardLoading}
              >
                {hazardLoading ? 'Reporting...' : 'Report Hazard'}
              </button>
            </>
          )}
        </div>
      )}

      {tab === 'sos' && (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ marginBottom: 24 }}>Emergency SOS</h2>
          {sosSent ? (
            <div className="form-success" style={{ fontSize: 18 }}>SOS sent! Help is on the way.</div>
          ) : (
            <button
              className="btn-danger"
              style={{ width: 200, height: 200, borderRadius: '50%', fontSize: 32, fontWeight: 800 }}
              onClick={sendSOS}
              disabled={sosLoading}
            >
              {sosLoading ? 'SENDING...' : 'SOS'}
            </button>
          )}
          <p style={{ marginTop: 16, color: 'var(--text-muted)' }}>Press to alert all school admins immediately</p>
        </div>
      )}
    </div>
  );
}
